// Structure: https://build.fhir.org/patient-definitions.html#Patient.communication.language

import { PatientCommunication } from "@medplum/fhirtypes";

import ISO6391 from "iso-639-1";

import { getCodeableConceptCode } from "@/utils/medplum";

import { LanguageCode } from "./enums/fhir-valuesets";
import { Systems } from "./enums/systems";

export function getPatientCommunication(language: string): PatientCommunication {
  return {
    language: {
      coding: [
        {
          system: Systems.AllLanguages,
          code: ISO6391.getCode(language),
          display: language,
        },
      ],
    },
  };
}

function isNotEnglishLanguageCode(languageCode: string | undefined): boolean {
  return languageCode !== undefined && languageCode !== LanguageCode.English;
}

/**
 * Determines whether a patient's communication preferences indicate that an
 * interpreter is needed. If a preferred language is recorded, it is used as
 * the sole signal. If no entry is marked preferred, any non-English language
 * in the list is treated as requiring an interpreter.
 *
 * @param communications - The full `Patient.communication` array, or undefined.
 * @returns `true` if an interpreter is required, `false` otherwise.
 */
export function requiresInterpreter(communications: PatientCommunication[] | undefined): boolean {
  if (!communications || communications.length === 0) {
    return false;
  }

  const preferredCommunication = communications.filter(
    (communication) => communication.preferred === true,
  );

  return preferredCommunication.some((communication) => {
    const languageCode = getCodeableConceptCode(communication.language);
    return isNotEnglishLanguageCode(languageCode);
  });
}
